/** @jsxImportSource @emotion/react */
"use client";

import type { MediaSizes } from "@packages/media";
import { useCurrentQueueItem, usePlayer } from "@packages/media";
import type { WithButtonWrapper } from "@packages/ui";
import type { MouseEventHandler } from "react";

import {
  coreMediaStyles,
  playbackCtlContainerStyles,
  useMediaClass,
} from "../../../utils/media.emotion";
import { FragmentAsChild } from "../../primitives/FragmentAsChild";
import { BackMediaButton } from "../controls/BackMediaButton.emotion";
import { CloseButton } from "../controls/CloseButton.emotion";
import { ForwardButton } from "../controls/ForwardButton.emotion";
import { PlayPause } from "../controls/PlayPause.emotion";

export type PlaybackControlsProps = WithButtonWrapper<{
  size: MediaSizes;
  onClose: MouseEventHandler<HTMLButtonElement>;
  showControls: boolean;
}>;

const SECS_BEFORE_HIDING_RESTART_BUTTON = 10;

export const PlaybackControls = ({
  Wrapper = FragmentAsChild,
  size,
  onClose,
  showControls,
}: PlaybackControlsProps) => {
  const player = usePlayer();
  const { currentItem } = useCurrentQueueItem();
  const mediaClass = useMediaClass();

  const canSkipIntro =
    player &&
    player.currentTime < 15 &&
    currentItem?.selected_audio.intro_ends_at &&
    player.currentTime < currentItem.selected_audio.intro_ends_at / 1000;

  const canRestart =
    currentItem?.selected_audio?.resumes_at &&
    player &&
    player.autoResumed &&
    player.currentTime <
      currentItem.selected_audio.resumes_at / 1000 +
        SECS_BEFORE_HIDING_RESTART_BUTTON;

  let backButton = null;
  if (!mediaClass.includes("collapsed-mobile")) {
    if (currentItem?.type === "radio_song") {
      backButton = (
        <div
          css={
            mediaClass.includes("collapsed-desktop")
              ? coreMediaStyles.playbackButtonPlaceholderS
              : coreMediaStyles.playbackButtonPlaceholder
          }
        />
      );
    } else {
      backButton = (
        <BackMediaButton
          canRestart={canRestart}
          Wrapper={Wrapper}
          backCSS={[
            coreMediaStyles.playbackButton,
            mediaClass.includes("expanded") || mediaClass.includes("fullscreen")
              ? coreMediaStyles.playbackButtonL
              : null,
          ]}
          restartCSS={
            mediaClass.includes("expanded")
              ? [coreMediaStyles.expandedWordButton]
              : []
          }
        />
      );
    }
  }

  return (
    <div
      css={[
        coreMediaStyles.playbackControlSet,
        playbackCtlContainerStyles[mediaClass],
        showControls ? coreMediaStyles.clickable : null,
      ]}
    >
      {backButton}
      <Wrapper asChild>
        <PlayPause tabIndex={0} />
      </Wrapper>
      {size === "collapsed-mobile" ? (
        <CloseButton
          onClick={onClose}
          css={[
            coreMediaStyles.playbackButton,
            mediaClass.includes("expanded") || mediaClass.includes("fullscreen")
              ? coreMediaStyles.playbackButtonL
              : null,
          ]}
        />
      ) : (
        <ForwardButton
          canSkipIntro={canSkipIntro}
          Wrapper={Wrapper}
          forwardCSS={[
            coreMediaStyles.playbackButton,
            mediaClass.includes("expanded") || mediaClass.includes("fullscreen")
              ? coreMediaStyles.playbackButtonL
              : null,
          ]}
        />
      )}
    </div>
  );
};
